// Flow configuration system to support multiple user journeys
// This allows us to share components while having different flows

export const FLOW_TYPES = {
  INSURANCE_PROFILE: "insurance-profile",
  EXTENDED_WARRANTY: "extended-warranty",
  INSURANCE_RENEWAL: "insurance-renewal",
  REFINANCING: "refinancing",
};

// Insurance Profile Flow Configuration (existing)
const INSURANCE_PROFILE_CONFIG = {
  flowType: FLOW_TYPES.INSURANCE_PROFILE,
  baseRoute: "/onboarding",
  defaultRoute: "/onboarding/welcome",

  steps: {
    WELCOME: "welcome",
    SURVEY: "survey",
    INSURANCE_ALTERNATIVES: "insurance-alternatives",
    VERIFICATION: "verification",
    DIMO: "dimo",
    CONTACT_INFO: "contact-info",
    POST_AUTH_SURVEY: "post-auth-survey",
    INSURANCE_OPTIONS: "insurance-options",
    DOCUMENT: "document",
    DOCUMENT_REUPLOAD: "document-reupload",
    CANOPY: "canopy",
    REVIEW_SUBMIT: "review-submit",
    SUCCESS: "success",
  },

  // Survey configuration
  surveys: {
    initial: "STEM_FwXtLCa2fL",
    postAuth: "STEM_WPFZm8Kfn1",
  },

  // UI configuration
  ui: {
    showProgressIndicator: true,
    progressExcludeSteps: [
      "welcome",
      "review-submit",
      "verification",
      "dimo",
      "success",
      "insurance-alternatives",
    ],
    welcomeBackground: "bg-gray-50",
    partnerNameKey: "partnerName", // Key for partner name in display config
  },

  // Feature flags
  features: {
    enableDimo: true, // Can be overridden by referrer logic
    enableCanopy: true,
    enableDocumentUpload: true,
  },
};

// Extended Warranty Flow Configuration (new)
const EXTENDED_WARRANTY_CONFIG = {
  flowType: FLOW_TYPES.EXTENDED_WARRANTY,
  baseRoute: "/extended-warranty",
  defaultRoute: "/extended-warranty/welcome",

  steps: {
    WELCOME: "welcome",
    QUALIFICATION: "qualification", // Car age check (>4 years)
    VERIFICATION: "verification",
    SURVEY: "survey", // Extended warranty specific survey
    CONTACT_INFO: "contact-info",
    EXTENDED_WARRANTY_CHOICE: "extended-warranty-choice", // NEW: Choice between policy upload and mileage check
    EXTENDED_WARRANTY_POLICY: "extended-warranty-policy", // NEW: Policy document upload
    EXTENDED_WARRANTY_MILEAGE: "extended-warranty-mileage", // NEW: Mileage check-in with photos
    VEHICLE_CHECK: "vehicle-check", // Check existing vehicle data via blackbox API
    VEHICLE_SELECTION: "vehicle-selection", // If existing data, let user choose vehicle
    VEHICLE_COMPLETION: "vehicle-completion", // Complete vehicle information
    EXTENDED_WARRANTY_OFFER: "extended-warranty-offer",
    EXTENDED_WARRANTY_HANDOFF: "extended-warranty-handoff",
    DOCUMENT: "document", // Policy upload to extract VIN
    PHOTO_CAPTURE: "photo-capture", // License plate + odometer photos
    HANDOFF: "handoff", // Redirect to chaiz.com with VIN data
    HANDOFF_TEST: "handoff-test", // Test route for handoff
    EXIT: "exit", // Exit screen for uninterested users
    SUCCESS: "success", // NEW: Success screen
  },
  // Survey configuration
  surveys: {
    main: "STEM_WzC3d0eB9Z", // Extended Warranty survey
  },

  // UI configuration
  ui: {
    showProgressIndicator: true,
    progressExcludeSteps: [
      "welcome",
      "qualification",
      "verification",
      "extended-warranty-choice",
      "extended-warranty-offer",
      "success",
      "extended-warranty-handoff",
      "handoff",
      "handoff-test",
    ], // NEW: Exclude choice and success screens
    welcomeBackground: "bg-blue-50", // Different styling
    partnerNameKey: "warrantyPartnerName",
  },

  // Feature flags
  features: {
    enableDimo: true,
    enablePolicyUpload: true,
    enablePhotoCapture: true,
    enableBlackboxAPI: true,
  },

  // Extended warranty specific config
  warranty: {
    minCarAge: 4, // Minimum car age in years
    chaizBaseUrl: "https://www.chaiz.com",
    blackboxApiEndpoint: "/api/users/vehicles/all", // TBD - endpoint for vehicle data
  },
};

const REFINANCING_CONFIG = {
  flowType: FLOW_TYPES.REFINANCING,
  baseRoute: "/refinance",
  defaultRoute: "/refinance/welcome",

  steps: {
    WELCOME: "welcome",
    VERIFICATION: "verification",
    REFINANCING_SURVEY: "refinance-survey",
    REFINANCING_CHECK_OFFERS: "refinance-check-offers",
    CONTACT_INFO: "contact-info",
    REFINANCING_POLICY_UPLOAD: "refinancing-upload-policy",
    REFINANCING_POLICY: "refinancing-policy", // NEW: Policy document upload
    REFINANCING_OFFER: "refinancing-offer",
    REFINANCING_HANDOFF: "refinancing-handoff",
    REFINANCING_VEHICLE_SELECTION: "refinancing-vehicle-selection", // If existing data, let user choose vehicle
    REFINANCING_VEHICLE_COMPLETION: "refinancing-vehicle-completion", // Complete vehicle information
    DOCUMENT: "document", // Policy upload to extract VIN
    HANDOFF: "handoff", // Redirect to caribou.com with VIN data
    HANDOFF_TEST: "handoff-test", // Test route for handoff
    EXIT: "exit", // Exit screen for uninterested users
    SUCCESS: "success", // NEW: Success screen
  },
  // Survey configuration
  surveys: {
    main: "STEM_WzC3d0eB9Z", // Extended Warranty survey
  },

  // UI configuration
  ui: {
    showProgressIndicator: true,
    progressExcludeSteps: [
      "welcome",
      "verification",
      "success",
      "refinance-check-offers",
      "refinancing-offer",
      "refinancing-handoff",
    ], // NEW: Exclude choice and success screens
    welcomeBackground: "bg-blue-50", // Different styling
  },

  // Feature flags
  features: {},
};

const INSURANCE_RENEWAL_CONFIG = {
  flowType: FLOW_TYPES.INSURANCE_RENEWAL,
  baseRoute: "/renewal",
  defaultRoute: "/renewal/welcome",

  steps: {
    WELCOME: "welcome",
    VERIFICATION: "verification",
    POLICIES: "policies",
    RENEWAL_SURVEY: "renewal-survey",
    CONTACT_INFO: "contact-info",
    SUCCESS_RENEWAL: "success-renewal", // NEW: Success screen
  },

  // Survey configuration
  surveys: {
    main: "STEM_WzC3d0eB9Z", // Extended Warranty survey
  },

  // UI configuration
  ui: {
    showProgressIndicator: true,
    progressExcludeSteps: [
      "welcome",
      "verification",
      "success",
      "success-renewal",
    ], // NEW: Exclude choice and success screens
    welcomeBackground: "bg-blue-50", // Different styling
    partnerNameKey: "warrantyPartnerName",
  },
};

// Export the configurations
export const FLOW_CONFIGS = {
  [FLOW_TYPES.INSURANCE_PROFILE]: INSURANCE_PROFILE_CONFIG,
  [FLOW_TYPES.EXTENDED_WARRANTY]: EXTENDED_WARRANTY_CONFIG,
  [FLOW_TYPES.INSURANCE_RENEWAL]: INSURANCE_RENEWAL_CONFIG,
  [FLOW_TYPES.REFINANCING]: REFINANCING_CONFIG,
};

// Helper functions
export const getFlowConfig = (flowType) => {
  return FLOW_CONFIGS[flowType] || FLOW_CONFIGS[FLOW_TYPES.INSURANCE_PROFILE];
};

export const getStepRoute = (flowType, step) => {
  const config = getFlowConfig(flowType);
  return `${config.baseRoute}/${step}`;
};

// Subdomain to flow type mapping
const SUBDOMAIN_FLOW_MAP = {
  // Explicit flow indicators
  'insurance': FLOW_TYPES.INSURANCE_PROFILE,
  'extended-warranty': FLOW_TYPES.EXTENDED_WARRANTY,
  'warranty': FLOW_TYPES.EXTENDED_WARRANTY,
  'refinance': FLOW_TYPES.REFINANCING,
  'refi': FLOW_TYPES.REFINANCING,
  'renewal': FLOW_TYPES.INSURANCE_RENEWAL,

  // Partner subdomains that default to insurance flow
  'dimo': FLOW_TYPES.INSURANCE_PROFILE,
  'bbmc': FLOW_TYPES.INSURANCE_PROFILE,
};

// Flow detection helper
export const detectFlowType = () => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  const parts = hostname.split('.');
  const subdomain = parts[0]; // e.g., 'insurance', 'dimo', 'warranty'

  // 1. Check subdomain mapping first
  if (SUBDOMAIN_FLOW_MAP[subdomain]) {
    return SUBDOMAIN_FLOW_MAP[subdomain];
  }

  // 2. Fallback to pathname for direct access or localhost
  if (pathname.startsWith('/extended-warranty')) {
    return FLOW_TYPES.EXTENDED_WARRANTY;
  }
  if (pathname.startsWith('/renewal')) {
    return FLOW_TYPES.INSURANCE_RENEWAL;
  }
  if (pathname.startsWith('/refinance')) {
    return FLOW_TYPES.REFINANCING;
  }

  // 3. Default to insurance profile
  return FLOW_TYPES.INSURANCE_PROFILE;
};
